import { ComplianceCaseTypeEnum as CaseTypeEnum } from "~/graphql/supergraphEnums";
import {
  type ComplianceCaseAction,
  type ComplianceCaseStatus,
  type ComplianceCaseSubStatus,
  type ComplianceCaseTypeEnum,
} from "~/graphql/supergraphTypes";
import { isInternalEscalationSource } from "../../../utils/constants";

export type NewActionKey = Extract<
  ComplianceCaseAction,
  | "propose_resolved"
  | "propose_escalation"
  | "propose_invalid"
  | "propose_deficient"
  | "de_escalate"
  | "propose_acceptance"
  | "propose_rejection"
  | "propose_approved"
  | "propose_denied"
  | "propose_waive"
>;

type ActionLabelSet = {
  [key in CaseTypeEnum]?: string;
} & {
  default: string;
};

const NEW_ACTION_LABELS_MAP: Record<NewActionKey, ActionLabelSet> = {
  propose_approved: {
    default: "Mark Approved",
  },
  propose_denied: {
    default: "Mark Rejected",
  },
  propose_resolved: {
    default: "Mark Complete - no matches",
  },
  propose_escalation: {
    default: "Mark need for GP escalation",
  },
  propose_invalid: {
    default: "Mark invalid",
  },
  propose_deficient: {
    default: "Mark need for LP data/doc collection",
  },
  de_escalate: {
    default: "De-escalate",
  },
  propose_acceptance: {
    default: "Mark GP verdict: approved",
  },
  propose_rejection: {
    default: "Mark GP verdict: rejected",
  },
  propose_waive: {
    default: "Propose Waive",
  },
};

const ACTION_STATUS_SUBSTATUS_LABELS_MAP = {
  // transaction monitoring and some generic investor onboarding actions
  resolved: "Mark resolved",
  return: "Return",
  escalated: "Mark need for escalation",
  invalid: "Mark invalid",
  cleared: "CO verdict: resolved",
  halted: "CO verdict: halt",
  de_escalated: "De-escalate",
  approved: "Mark accepted",
  rejected: "Mark rejected",
  proceed: {
    in_progress: "Mark complete - no deficiencies",
    under_review: {
      "": "Case marked approved - [NO GP VERDICT RENDERED]",
      accepted: "Case marked approved - [GP VERDICT APPROVE]",
      rejected: "Case marked rejected - [GP VERDICT REJECT",
    },
  },
  escalate: {
    under_review: {
      proposed_fund_escalation: "Confirm escalation",
    },
  },
  accept: {
    pending_fund_review: "Mark GP verdict: approved",
  },
  reject: {
    pending_fund_review: "Mark GP verdict: reject",
  },
  de_escalate: {
    pending_fund_review: "De-escalate",
    pending_data_resubmission: "De-escalate",
  },
  propose_deficient: "Mark need for LP data/doc collection",
  propose_fund_escalation: "Mark need for GP escalation",
  propose_invalid: "Mark invalid",
  propose_waive: "Mark compliance waived",
  propose_defer: "Mark compliance deferred",
} as const;

const MARKED_SUCCESS_LABELS_MAP = {
  resolved: "Proposed resolved",
  invalid: "Proposed invalidation",
  escalated: "Proposed escalation",
  cleared: "CO verdict: resolved",
  halted: "CO verdict: halt",
  de_escalated: "Manually de-escalated",
  approved: "Proposed acceptance",
  rejected: "Proposed rejection",
  proceed: "Proposed complete - no deficiencies",
  propose_deficient: "Proposed need for LP data/doc collection",
  propose_fund_escalation: "Proposed need for GP escalation",
  propose_invalid: "Proposed invalidation",
  propose_resolved: "Proposed resolved",
  propose_escalation: "Proposed GP escalation",
  propose_acceptance: "Marked GP verdict: approved",
  propose_rejection: "Marked GP verdict: rejected",
  propose_waive: "Proposed compliance waived",
  propose_defer: "Proposed compliance deferred",
  de_escalate: "Manually de-escalated",
  accept: "Marked GP verdict: approved",
  reject: "Marked GP verdict: rejected",
} as const;

export type MarkSuccessKey = keyof typeof MARKED_SUCCESS_LABELS_MAP;

export const getLabel = (
  key: string,
  status?: ComplianceCaseStatus | null,
  substatus: ComplianceCaseSubStatus | undefined | null = null,
): string => {
  const label = (ACTION_STATUS_SUBSTATUS_LABELS_MAP as Record<string, unknown>)[key];
  if (typeof label === "string") {
    return label;
  } else if (label && status) {
    const statusLabels = (label as Record<string, unknown>)[status];
    if (typeof statusLabels === "string") {
      return statusLabels;
    } else if (statusLabels && typeof statusLabels === "object") {
      /** this is actually unlikely to be hit by a maker.
       * Logic is sound if we come across a time where a maker needs to worry about substatus and status */
      const subLabels = statusLabels as Record<string, string>;
      if (substatus && subLabels[substatus]) {
        return subLabels[substatus];
      } else if (subLabels[""]) {
        return subLabels[""];
      }
    }
  }
  return "";
};

export const getMarkSuccessLabel = (key: MarkSuccessKey): string => {
  if (MARKED_SUCCESS_LABELS_MAP[key]) {
    return MARKED_SUCCESS_LABELS_MAP[key];
  } else {
    // would return a generic label "Case updated"
    return "Case updated";
  }
};

export const getNewActionLabel = (
  action: NewActionKey,
  caseType: ComplianceCaseTypeEnum,
): string => {
  const actionLabels = NEW_ACTION_LABELS_MAP[action];
  return caseType && actionLabels[caseType] ? actionLabels[caseType] : actionLabels.default;
};

export const isNewActionKey = (action: string): action is NewActionKey =>
  Object.prototype.hasOwnProperty.call(NEW_ACTION_LABELS_MAP, action);

const INTERNAL_SOURCE_ACTION_LABELS: Partial<Record<string, string>> = {
  propose_escalation: "Mark need for escalation",
};

const INTERNAL_ESCALATION_ACTION_LABELS: Partial<Record<string, string>> = {
  propose_acceptance: "Internally approved",
  propose_rejection: "Internally rejected",
};

const INTERNAL_SOURCE_SUCCESS_LABELS: Partial<Record<string, string>> = {
  propose_escalation: "Proposed escalation",
};

const INTERNAL_ESCALATION_SUCCESS_LABELS: Partial<Record<string, string>> = {
  propose_acceptance: "Marked internally approved",
  propose_rejection: "Marked internally rejected",
};

const internalLabel = (
  sourceLabels: Partial<Record<string, string>>,
  escalationLabels: Partial<Record<string, string>>,
  action: string,
  caseType: ComplianceCaseTypeEnum | null | undefined,
  sourceCaseType: ComplianceCaseTypeEnum | null | undefined,
): string | undefined => {
  if (isInternalEscalationSource(caseType)) return sourceLabels[action];
  if (caseType === CaseTypeEnum.Escalation && isInternalEscalationSource(sourceCaseType)) {
    return escalationLabels[action];
  }
  return undefined;
};

/**
 * Internal-escalation wording (entity/client onboarding subject): the onboarding case's own
 * escalation action and its escalation children's verdicts. Undefined means keep the default label.
 */
export const getInternalActionLabel = (
  action: string,
  caseType: ComplianceCaseTypeEnum | null | undefined,
  sourceCaseType: ComplianceCaseTypeEnum | null | undefined,
): string | undefined =>
  internalLabel(
    INTERNAL_SOURCE_ACTION_LABELS,
    INTERNAL_ESCALATION_ACTION_LABELS,
    action,
    caseType,
    sourceCaseType,
  );

export const getInternalMarkSuccessLabel = (
  action: string,
  caseType: ComplianceCaseTypeEnum | null | undefined,
  sourceCaseType: ComplianceCaseTypeEnum | null | undefined,
): string | undefined =>
  internalLabel(
    INTERNAL_SOURCE_SUCCESS_LABELS,
    INTERNAL_ESCALATION_SUCCESS_LABELS,
    action,
    caseType,
    sourceCaseType,
  );

export const NEW_ACTION_LABEL_CASE_TYPES: CaseTypeEnum[] = [
  CaseTypeEnum.RecurringHit,
  CaseTypeEnum.Escalation,
  CaseTypeEnum.PaymentInstructionChangeReview,
  CaseTypeEnum.Remediation,
  CaseTypeEnum.ChangeInCircumstance,
  CaseTypeEnum.DocumentRefresh,
  CaseTypeEnum.ProfileRefresh,
  CaseTypeEnum.EntityOnboarding,
  CaseTypeEnum.ClientOnboarding,
];

export const gpCaseTypes: ComplianceCaseTypeEnum[] = [
  CaseTypeEnum.GpSourceOfFunds,
  CaseTypeEnum.GpTransactionMonitoring,
];
